// RateizzazioniView.jsx — amm-v3 (A5, ondata 32): la vista "Piani di rientro"
// (pagina 'rateizzazioni') in React, flag-gated (shell_amm_v3_rateizzazioni,
// default OFF).
//
// Replica 1:1 renderRateizzazioni di admin-module.js r.7396-7433: titolo con
// tooltip tecnico, bottone "+ Nuovo piano", 3 KPI (attivi/residuo/totali),
// tabella (Oggetto+badge decaduta, Ente, Totale, Rate, Residuo, Prossima,
// Stato, elimina-admin). Riga cliccabile ⇒ dettaglio (rate editabili) V1.
//
// CONFINE (dichiarato): editor "+ Nuovo piano" (editRateizzazione) e DETTAGLIO
// con RATE EDITABILI (openRateizzazione → openRataEdit/rate.update, dove vivono
// gli invarianti CAS/doppio-salva/no-perdita-dati/rete-KO-rumorosa) restano V1;
// il v3 li DELEGA. "+ Nuovo" riceve dal v3 il proprio onSaved (refresh v3);
// il dettaglio si auto-gestisce il reload ⇒ mai renderRateizzazioni diretto
// (niente bypass/interceptor).
//
// DEVIAZIONI DICHIARATE:
//  D-A5.2 doppio click su "+ Nuovo"/"elimina" = 1 azione (guardia ref).
//  D-A5.3 caricamento fallito ⇒ StatoBox errore + Riprova (nessun dato) o
//         banner + dati conservati (famiglia D-A1.5). Il v1 non ha questo caso
//         (la lista è un solo fetch che, se fallisce, lascia la pagina rotta).

import React from 'react';
import { DataTable } from '../ui/DataTable.jsx';
import { AM_NEW_TEXTS } from './api.js';
import {
  RZ_TEXTS, kpiDefs, rateProgress, prossimaData, residuoRiga,
  readRateizzazioni, apriNuovoPiano, apriDettaglio, eliminaPiano,
} from './rateizzazioni-api.js';

function V1Html({ html }) {
  if (!html) return null;
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

export function RateizzazioniView({ win, tick }) {
  const A = win.AMM;
  const [dati, setDati] = React.useState({ loading: true, error: null, list: null });
  const [reloadN, setReloadN] = React.useState(0);
  const busyRef = React.useRef({}); // guardia per-azione (D-A5.2)

  React.useEffect(() => {
    let vivo = true;
    setDati((d) => ({ ...d, loading: true, error: null }));
    readRateizzazioni(win).then(
      (list) => { if (vivo) setDati({ loading: false, error: null, list }); },
      (e) => { if (vivo) setDati((d) => ({ ...d, loading: false, error: e })); }
    );
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win, tick, reloadN]);

  const list = dati.list;
  const reload = () => setReloadN((n) => n + 1);

  const onNuovo = () => {
    if (busyRef.current.nuovo) return;
    busyRef.current.nuovo = true;
    try { apriNuovoPiano(win, () => { busyRef.current.nuovo = false; reload(); }); }
    finally { setTimeout(() => { busyRef.current.nuovo = false; }, 400); } // il modale può chiudersi senza salvare
  };

  const onDelete = (id) => {
    const k = 'd' + id;
    if (busyRef.current[k]) return;
    if (!win.confirm(RZ_TEXTS.confirmElimina)) return;
    busyRef.current[k] = true;
    eliminaPiano(win, id, () => { A.toast(RZ_TEXTS.toastEliminato); busyRef.current[k] = false; reload(); })
      .catch(() => { busyRef.current[k] = false; });
  };

  const kpis = kpiDefs(list || [], A.fmtEur2);

  const columns = [
    {
      key: 'oggetto', label: RZ_TEXTS.colOggetto,
      render: (p) => {
        const decaduta = p.riepilogo && p.riepilogo.decaduta;
        return (
          <span className="font-medium">
            {p.oggetto || RZ_TEXTS.dash}
            {decaduta ? <>{' '}<span className="amm-badge" style={{ background: '#ffe4e6', color: '#9f1239' }}>{RZ_TEXTS.decaduta}</span></> : null}
          </span>
        );
      },
    },
    { key: 'ente', label: RZ_TEXTS.colEnte, render: (p) => <span className="text-slate-500">{p.ente || RZ_TEXTS.dash}</span> },
    { key: 'importo_totale', label: RZ_TEXTS.colTotale, render: (p) => A.fmtEur2(p.importo_totale) },
    { key: 'rate', label: RZ_TEXTS.colRate, render: (p) => <span className="tabular-nums">{rateProgress(p)}</span> },
    { key: 'residuo', label: RZ_TEXTS.colResiduo, render: (p) => <span className="font-medium">{residuoRiga(p, A.fmtEur2)}</span> },
    { key: 'prossima', label: RZ_TEXTS.colProssima, render: (p) => <span className="text-slate-500">{prossimaData(p)}</span> },
    { key: 'stato', label: RZ_TEXTS.colStato, render: (p) => <V1Html html={A.pianoStatoBadge(p.stato)} /> },
    {
      key: '__act', label: '', align: 'right', filter: false,
      render: (p) => (A.isAdmin()
        ? <button type="button" className="amm-rateiz-del text-rose-600 hover:text-rose-800 text-xs" data-id={p.id} onClick={() => onDelete(p.id)}>{RZ_TEXTS.elimina}</button>
        : null),
    },
  ];

  return (
    <div data-amm-v3="rateizzazioni">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold" title={RZ_TEXTS.titleTooltip}>{RZ_TEXTS.title}</h1>
        <button id="amm-rateiz-new" type="button" className="bg-cyan-700 hover:bg-cyan-600 text-white text-sm px-3 py-2 rounded font-medium" onClick={onNuovo}>{RZ_TEXTS.nuovoPiano}</button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        {kpis.map((k) => (
          <div key={k.label} className="bg-white border rounded-lg p-4">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">{k.label}</div>
            <div className="text-lg font-semibold mt-0.5">{k.val}</div>
          </div>
        ))}
      </div>

      {/* D-A5.3: errore con righe presenti ⇒ banner + Riprova, dati conservati */}
      {dati.error && list && list.length ? (
        <div role="alert" className="mb-3 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-between gap-3" style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
          <span>{AM_NEW_TEXTS.erroreRicarica + (dati.error.message || String(dati.error))}</span>
          <button type="button" style={{ background: '#0e7490', color: '#fff', border: 0, borderRadius: 8, padding: '6px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }} onClick={reload}>{AM_NEW_TEXTS.riprova}</button>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={list || []}
        rowKey="id"
        onRowClick={(p) => apriDettaglio(win, p.id)}
        loading={dati.loading}
        error={list && list.length ? null : dati.error}
        onRetry={reload}
        emptyMessage={RZ_TEXTS.empty}
      />
    </div>
  );
}

// Factory (pattern createAmmV3Esercizi)
export function createAmmV3Rateizzazioni(win) {
  let root = null;
  let mountedEl = null;
  let tick = 0;

  function render() {
    const container = win.document.getElementById('amm-page');
    if (!container) throw new Error('#amm-page assente');
    const manomesso = root && mountedEl === container && tick > 0 && !container.querySelector('[data-amm-v3]');
    if (!root || mountedEl !== container || !container.isConnected || manomesso) {
      if (root) { try { root.unmount(); } catch (e) { /* root già ferito/smontato */ } }
      container.replaceChildren();
      root = win.ReactDOM.createRoot(container);
      mountedEl = container;
    }
    tick += 1;
    root.render(<RateizzazioniView win={win} tick={tick} />);
  }

  function unmount() {
    if (root) {
      try { root.unmount(); } catch (e) { /* già smontato */ }
      root = null;
    }
    mountedEl = null;
  }

  function isMounted() { return !!root; }

  return { render, unmount, isMounted };
}
